import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useVenuesList } from "./use-venues-list";
import { VenuesFilters } from "./VenuesFilters";
import { VenuesTable } from "./VenuesTable";
import { VenuesTableSkeleton } from "./VenuesTableSkeleton";
import { VenuesListEmpty } from "./VenuesListEmpty";
import { VenuesPagination } from "./VenuesPagination";
import { DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from "./constants";
import type { VenueStatusFilter, VenueTypeFilter } from "./types";

export function VenuesPage() {
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<VenueStatusFilter>("all");
  const [type, setType] = useState<VenueTypeFilter>("all");
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever a filter that changes the result set
  // changes — otherwise narrowing the filter can strand the table on an
  // empty page.
  useEffect(() => {
    setPage(1);
  }, [query, status, type]);

  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

  const filters = {
    query: debouncedQuery,
    status,
    type,
    page,
    limit: DEFAULT_PAGE_SIZE,
  };

  const { data, isLoading, isFetching, isError } = useVenuesList(filters);
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  // Derived from the *debounced* query on purpose — it must agree with the
  // query that actually produced `data`. Using the raw `query` here would
  // flash the "no results" empty-state during the debounce window, before
  // the request for the new term has even fired. `VenuesFilters` computes
  // its own `hasFilters` from the raw query (to toggle the reset button
  // instantly) — the two are intentionally out of phase; don't unify them.
  const hasFilters = debouncedQuery.length > 0 || status !== "all" || type !== "all";

  const handleReset = () => {
    setQuery("");
    setStatus("all");
    setType("all");
  };

  return (
    <div data-testid="venues-screen" className="space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("venuesDirectory.eyebrow")}</p>
        <h1 className="text-4xl">{t("venuesDirectory.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("venuesDirectory.subtitle")}
        </p>
      </header>

      <VenuesFilters
        query={query}
        status={status}
        type={type}
        onQueryChange={setQuery}
        onStatusChange={setStatus}
        onTypeChange={setType}
        onReset={handleReset}
      />

      {isLoading && <VenuesTableSkeleton />}

      {isError && (
        <div
          data-testid="venues-list-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("venuesDirectory.list.error")}
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && <VenuesListEmpty hasFilters={hasFilters} />}

      {!isError && items.length > 0 && (
        <div
          data-testid="venues-list-container"
          aria-busy={isFetching}
          className={isFetching ? "opacity-70 transition-opacity" : ""}
        >
          <VenuesTable items={items} />
          <div className="mt-4">
            <VenuesPagination
              page={page}
              limit={DEFAULT_PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}
    </div>
  );
}
